/**
 * @module components/SiteChrome
 * @description Demo notice, header (logo + cart button) and footer.
 *
 * In production Tiers 1–2 render INSIDE theafj.org's existing WordPress
 * header/footer (brief §2). These stand-ins only frame the standalone demo.
 */
import type { JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { navigate } from "../app/router";
import { DEMO_NOTICE } from "../config/page";
import { CURRENT_TIER, TIER_NAMES } from "../config/tier";
import { formatUsd } from "../lib/format";
import { Icon } from "./Icons";
import { TierTour } from "./TierTour";
import "./SiteChrome.css";

/** Thin, always-visible disclosure that this is a demo with sample content. */
export function DemoNotice(): JSX.Element {
    return (
        <div className="demo-notice" role="note">
            <strong>{TIER_NAMES[CURRENT_TIER]}</strong>
            <span>{DEMO_NOTICE}</span>
            <TierTour />
        </div>
    );
}

/** Sticky site header: AFJ logo and the cart button with live count. */
export function SiteHeader(): JSX.Element {
    const { lines, totalUsd, setCartOpen } = useGiving();
    return (
        <header className="site-header">
            <div className="container site-header__inner">
                <a href="#/" className="site-header__logo" onClick={(e) => { e.preventDefault(); navigate("browse"); }}>
                    <img src="brand/afj-horizontal.png" alt="The American Friends of Jamaica" width={184} height={34} />
                </a>
                <nav className="site-header__nav" aria-label="Demo navigation">
                    <span className="site-header__crumb">Ways to Give · <strong>Choose a Cause</strong></span>
                    <button type="button" className="site-header__cart" onClick={() => setCartOpen(true)} aria-label={`Open donation cart, ${lines.length} causes, ${formatUsd(totalUsd)}`}>
                        <Icon name="cart" size={22} />
                        {lines.length > 0 && <span className="site-header__count">{lines.length}</span>}
                    </button>
                </nav>
            </div>
        </header>
    );
}

/** Footer stand-in with AFJ's public registration facts. */
export function SiteFooter(): JSX.Element {
    return (
        <footer className="site-footer">
            <div className="container site-footer__inner">
                <img src="brand/afj-vertical.png" alt="" width={72} height={77} className="site-footer__mark" />
                <p>The American Friends of Jamaica, Inc. · 1697 Broadway, Suite 501, New York, NY 10019 · A registered 501(c)(3).</p>
                <p className="site-footer__built">Demo built by Island Bridge Solutions, LLC for AFJ review. Not AFJ's live site.</p>
            </div>
        </footer>
    );
}
